import { Component, inject, Input, OnInit, OnChanges, SimpleChanges, ViewChild, AfterViewInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HighlightChartComponent } from '../../../../../shared/components/charts/highlight-chart/highlight-chart.component';
import { ToggleBtnsComponent } from '../../../../../shared/components/toggle-btns/toggle-btns.component';
import { SelectionComponent, SelectionOption } from '../../../../../shared/components/ui/selection/selection.component';
import { businessHighlightsGainerData, businessHighlightsLoserData, trendingIndustriesData } from '../../../../../shared/utils/dummyData';
import { DateRangePickerOutput } from '../../../../../shared/components/date-picker/date-picker.component';
import { ApiService } from '../../../../../core/services/api.service';
import { IndividualToasterComponent } from "../../../../../shared/components/loader/loader.component";
import { IconComponentV2 } from '../../../../../shared/components/icon-component/icon.component';

interface HighlightChartData {
  label: string;
  frequencyA: number;
  frequencyB: number;
  currentPeriod?: string;
  previousPeriod?: string;
}

interface ToggleOption {
  label: string;
  icon?: string;
  tooltip?: string;
  isTable?: boolean;
  value?: string | number;
}

type DataType = 'highlights' | 'industries';
type HighlightsTab = 'gainers' | 'droppers';
type IndustriesTab = 'dental' | 'healthcare' | 'non_healthcare';

@Component({
  standalone: true,
  selector: 'app-business-highlights',
  imports: [CommonModule, HighlightChartComponent, ToggleBtnsComponent, SelectionComponent, IndividualToasterComponent, IconComponentV2],
  templateUrl: './business-highlights.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './business-highlights.component.scss'
})
export class BusinessHighlightsComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() dataType: DataType = 'highlights'; // Parent can specify 'highlights' or 'industries'
  @Input() dateRange!: DateRangePickerOutput;
  @Input() productServerName: string = 'denefits';
  @Input() requiresAccessToken: boolean = false;
  @Input() cocuId: number = 1;
  @Input() countryId: number = 1;
  isLoading = signal(false)
  @ViewChild(SelectionComponent) selectionComponent?: SelectionComponent<IndustriesTab>;
  
  // Active tab
  private activeTabSignal: WritableSignal<HighlightsTab | IndustriesTab> = signal('gainers');
  
  // Toggle options for tab switching - will be set based on dataType
  tabOptions: ToggleOption[] = [];
  
  // Selection options for industries mode
  industryOptions: SelectionOption<IndustriesTab>[] = [
    { value: 'dental', label: 'Dental' },
    { value: 'healthcare', label: 'Healthcare' },
    { value: 'non_healthcare', label: 'Non-Healthcare' }
  ];
  
  // Highlights data (Gainer/Dropper)
  gainerChartData: HighlightChartData[] = [];
  gainerCurrentPeriod: string = '';
  gainerPreviousPeriod: string = '';

  dropperChartData: HighlightChartData[] = [];
  dropperCurrentPeriod: string = '';
  dropperPreviousPeriod: string = '';

  // Industries data (Dental/Healthcare/Non-Healthcare)
  dentalChartData: HighlightChartData[] = [];
  healthcareChartData: HighlightChartData[] = [];
  nonHealthcareChartData: HighlightChartData[] = [];
  industryCurrentPeriod: string = '';
  industryPreviousPeriod: string = '';

  constructor(private apiService: ApiService) { }

  ngOnInit(): void {
    this.initializeTabOptions();
    this.loadData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['dateRange'] && changes['dateRange'].currentValue) || 
        (changes['productServerName'] && !changes['productServerName'].firstChange) ||
        (changes['cocuId'] && !changes['cocuId'].firstChange) ||
        (changes['countryId'] && !changes['countryId'].firstChange)) {
      // When dateRange or ID changes, reload data based on dataType
      this.fetchAllData();
    }
  }

  ngAfterViewInit(): void {
    // Set initial value for selection component in industries mode
    if (this.dataType === 'industries' && this.selectionComponent) {
      setTimeout(() => {
        const currentTab = this.activeTab as IndustriesTab;
        this.selectionComponent?.writeValue(currentTab);
      });
    }
  }

  get activeTab() {
    return this.activeTabSignal();
  }

  get title(): string {
    return this.dataType === 'highlights' ? 'Business Highlights' : 'Trending Industries';
  }

  get currentChartData(): any {
    if (this.dataType === 'highlights') {
      return this.activeTab === 'gainers' ? this.gainerChartData : this.dropperChartData;
    } else {
      // For industries
      const tab = this.activeTab as IndustriesTab;
      return tab === 'dental' ? this.dentalChartData : 
             tab === 'healthcare' ? this.healthcareChartData : 
             this.nonHealthcareChartData;
    }
  }

  get currentPeriod() {
    if (this.dataType === 'highlights') {
      return this.activeTab === 'gainers' ? this.gainerCurrentPeriod : this.dropperCurrentPeriod;
    } else {
      return this.industryCurrentPeriod;
    }
  }

  get previousPeriod() {
    if (this.dataType === 'highlights') {
      return this.activeTab === 'gainers' ? this.gainerPreviousPeriod : this.dropperPreviousPeriod;
    } else {
      return this.industryPreviousPeriod;
    }
  }

  setActiveTab(tab: HighlightsTab | IndustriesTab): void {
    this.activeTabSignal.set(tab);
  }

  onTabChange(value: string): void {
    if (this.dataType === 'highlights') {
      if (value === 'gainers' || value === 'droppers') {
        this.setActiveTab(value as HighlightsTab);
      }
    } else {
      if (value === 'dental' || value === 'healthcare' || value === 'non_healthcare') {
        this.setActiveTab(value as IndustriesTab);
      }
    }
  }

  onIndustryChange(value: IndustriesTab | IndustriesTab[]): void {
    // Handle single selection
    const singleValue = Array.isArray(value) ? value[0] : value;
    if (singleValue) {
      this.setActiveTab(singleValue);
    }
  }

  private initializeTabOptions(): void {
    if (this.dataType === 'highlights') {
      this.tabOptions = [
        {
          label: 'Top 10 Business Gainers',
          tooltip: 'View top gaining businesses',
          isTable: false,
          value: 'gainers'
        },
        {
          label: 'Top 10 Business Droppers',
          tooltip: 'View top declining businesses',
          isTable: true,
          value: 'droppers'
        },
      ];
      this.activeTabSignal.set('gainers');
    } else {
      this.tabOptions = [
        {
          label: 'Dental',
          tooltip: 'View dental industry trends',
          isTable: false,
          value: 'dental'
        },
        {
          label: 'Healthcare',
          tooltip: 'View healthcare industry trends',
          isTable: false,
          value: 'healthcare'
        },
        {
          label: 'Non-Healthcare',
          tooltip: 'View non-healthcare industry trends',
          isTable: true,
          value: 'non_healthcare'
        },
      ];
      this.activeTabSignal.set('dental');
    }
  }

  private loadData(): void {
    if (this.dataType === 'highlights') {
      this.loadGainerData();
      this.loadDropperData();
    } else {
      this.loadIndustryData();
    }
  }

  private fetchAllData(
    useCache: boolean = false,
    useLoader: boolean = false
  ) {
    if (this.dataType === 'highlights') {
      // Fetch gainers and losers
      this.fetchHighlightsData('business_highlight_gainers', useCache, useLoader);
      this.fetchHighlightsData('business_highlight_losers', useCache, useLoader);
    } else {
      // Fetch trending industries for all categories
      this.fetchIndustriesData(useCache, useLoader);
    }
  }

  private fetchHighlightsData(
    type: string,
    useCache?: boolean,
    useLoader: boolean = false
  ) {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }
    this.isLoading.set(true)
    if (type === 'business_highlight_gainers') {
      this.gainerChartData = [];
    } else if (type === 'business_highlight_losers') {
      this.dropperChartData = [];
    }
    const payload: any = {
      start_time: this.dateRange.start_date || new Date(),
      end_time: this.dateRange.end_date || new Date(),
      previous_start_time: this.dateRange.previous_start_date || null,
      previous_end_time: this.dateRange.previous_end_date || null,
      is_dummy: 0,
      // timezone: 'PDT',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      limit: 10, // Top 10
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }

    this.apiService
      .post(`/visionboard/stats/${type}`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
            this.isLoading.set(false)
           
            // Transform data for highlight chart
            const records = response.data.records || [];
            const transformedData: HighlightChartData[] = records.slice(0, 10).map((item: any) => ({
              label: item.practice_name || item.doctor_name || 'N/A',
              frequencyA: item.current_period_count || 0,
              frequencyB: item.previous_period_count || 0,
              currentPeriod: response.data.currentPeriod || this.formatPeriod(this.dateRange.start_date, this.dateRange.end_date),
              previousPeriod: response.data.previousPeriod || this.formatPeriod(this.dateRange.previous_start_date, this.dateRange.previous_end_date),
            }));

            // Update appropriate data based on type
            if (type === 'business_highlight_gainers') {
              this.gainerChartData = transformedData;
              this.gainerCurrentPeriod = response.data.currentPeriod || this.formatPeriod(this.dateRange.start_date, this.dateRange.end_date);
              this.gainerPreviousPeriod = response.data.previousPeriod || this.formatPeriod(this.dateRange.previous_start_date, this.dateRange.previous_end_date);
            } else if (type === 'business_highlight_losers') {
              this.dropperChartData = transformedData;
              this.dropperCurrentPeriod = response.data.currentPeriod || this.formatPeriod(this.dateRange.start_date, this.dateRange.end_date);
              this.dropperPreviousPeriod = response.data.previousPeriod || this.formatPeriod(this.dateRange.previous_start_date, this.dateRange.previous_end_date);
            }

          }
        },
        error: (error: any) => {
          console.error(`Error fetching ${type}:`, error);
          this.isLoading.set(false)
          // Fallback to dummy data on error
          if (type === 'business_highlight_gainers') {
            this.loadGainerData();
          } else {
            this.loadDropperData();
          }
        }
      });
  }

  private fetchIndustriesData(
    useCache?: boolean,
    useLoader: boolean = false
  ) {
    if (!this.dateRange) {
      console.warn('DateRange not available yet');
      return;
    }
this.isLoading.set(true)
    this.dentalChartData = [];
    this.healthcareChartData = [];
    this.nonHealthcareChartData = [];
    const payload: any = {
      start_time: this.dateRange.start_date || new Date(),
      end_time: this.dateRange.end_date || new Date(),
      previous_start_time: this.dateRange.previous_start_date || null,
      previous_end_time: this.dateRange.previous_end_date || null,
      is_dummy: 0,
      // timezone: 'PDT',
      unit: this.dateRange?.unit || 'day',
      outputFormat: 'db',
      limit: 10, // Top 10
      cocu_id: this.cocuId,
      country_id: this.countryId
    };

    if (this.productServerName === 'credee') {
      payload['timezone'] = 'PDT';
    }                                                                                                                                                         

    this.apiService
      .post(`/visionboard/stats/trending_industries`, payload, {
        addAccessToken: this.requiresAccessToken,
        productServerName: this.productServerName,
        showErrorToaster: true,
        showSuccessToaster: false,
        showLoader: false,
      })
      .subscribe({
        next: (response: any) => {
          if (response?.is_error === 0 && response?.data) {
            this.isLoading.set(false)
           

            // Set periods
            this.industryCurrentPeriod = response.data.currentPeriod || this.formatPeriod(this.dateRange.start_date, this.dateRange.end_date);
            this.industryPreviousPeriod = response.data.previousPeriod || this.formatPeriod(this.dateRange.previous_start_date, this.dateRange.previous_end_date);

            // Transform dental data
            this.dentalChartData = (response.data.dental || [])
              .slice(0, 10)
              .map((item: any) => ({
                label: item.speciality_name || 'N/A',
                frequencyA: parseInt(item.current_period_count) || 0,
                frequencyB: parseInt(item.previous_period_count) || 0,
                currentPeriod: this.industryCurrentPeriod,
                previousPeriod: this.industryPreviousPeriod,
              }));

            // Transform healthcare data
            this.healthcareChartData = (response.data.healthcare || [])
              .slice(0, 10)
              .map((item: any) => ({
                label: item.speciality_name || 'N/A',
                frequencyA: parseInt(item.current_period_count) || 0,
                frequencyB: parseInt(item.previous_period_count) || 0,
                currentPeriod: this.industryCurrentPeriod,
                previousPeriod: this.industryPreviousPeriod,
              }));

            // Transform non-healthcare data
            this.nonHealthcareChartData = (response.data.non_healthcare || [])
              .slice(0, 10)
              .map((item: any) => ({
                label: item.speciality_name || 'N/A',
                frequencyA: parseInt(item.current_period_count) || 0,
                frequencyB: parseInt(item.previous_period_count) || 0,
                currentPeriod: this.industryCurrentPeriod,
                previousPeriod: this.industryPreviousPeriod,
              }));
          }
        },
        error: (error: any) => {
          this.isLoading.set(false)
          console.error(`Error fetching trending_industries:`, error);
          // Fallback to dummy data on error
          this.loadIndustryData();
        }
      });
  }

  private formatPeriod(startDate: string | null | undefined, endDate: string | null | undefined): string {
    if (!startDate || !endDate) return '';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    const formatDate = (date: Date) => {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    };

    return `${formatDate(start)} to ${formatDate(end)}`;
  }

  private loadGainerData(): void {
    // Transform gainer data for highlight-chart (top 10)
    const transformedData: HighlightChartData[] = businessHighlightsGainerData
      .slice(0, 10)
      .map((item: any, index: number) => ({
        label: `${item.practice_name || item.doctor_name || 'N/A'}`,
        frequencyA: item.current_period_count,
        frequencyB: item.previous_period_count,
        currentPeriod: undefined,
        previousPeriod: undefined,
      }));

    this.gainerChartData = transformedData;
    
    // Set periods based on typical data range
    this.gainerCurrentPeriod = 'Jul 27, 2025 to Oct 27, 2025';
    this.gainerPreviousPeriod = 'Apr 26, 2025 to Jul 26, 2025';
  }

  private loadDropperData(): void {
    // Transform dropper/loser data for highlight-chart (top 10)
    const transformedData: HighlightChartData[] = businessHighlightsLoserData.records
      .slice(0, 10)
      .map((item: any, index: number) => ({
        label: `${item.practice_name || item.doctor_name || 'N/A'}`,
        frequencyA: item.current_period_count,
        frequencyB: item.previous_period_count,
        currentPeriod: businessHighlightsLoserData.currentPeriod,
        previousPeriod: businessHighlightsLoserData.previousPeriod,
      }));

    this.dropperChartData = transformedData;
    this.dropperCurrentPeriod = businessHighlightsLoserData.currentPeriod;
    this.dropperPreviousPeriod = businessHighlightsLoserData.previousPeriod;
  }

  private loadIndustryData(): void {
    // Transform industry data for highlight-chart (top 10 from each category)
    this.industryCurrentPeriod = trendingIndustriesData.currentPeriod;
    this.industryPreviousPeriod = trendingIndustriesData.previousPeriod;

    // Dental data
    this.dentalChartData = trendingIndustriesData.dental
      .slice(0, 10)
      .map((item: any) => ({
        label: item.speciality_name,
        frequencyA: parseInt(item.current_period_count) || 0,
        frequencyB: parseInt(item.previous_period_count) || 0,
      }));

    // Healthcare data
    this.healthcareChartData = trendingIndustriesData.healthcare
      .slice(0, 10)
      .map((item: any) => ({
        label: item.speciality_name,
        frequencyA: parseInt(item.current_period_count) || 0,
        frequencyB: parseInt(item.previous_period_count) || 0,
      }));

    // Non-Healthcare data
    this.nonHealthcareChartData = trendingIndustriesData.non_healthcare
      .slice(0, 10)
      .map((item: any) => ({
        label: item.speciality_name,
        frequencyA: parseInt(item.current_period_count) || 0,
        frequencyB: parseInt(item.previous_period_count) || 0,
      }));
  }
}
